/**
 *
 * ProductDetailsPage
 *
 */

import {
  selectAddedProducts,
  selectAddingProducts,
  selectBreadCrumbDataMarkup,
  selectStore,
  selectUserPermissions,
} from 'containers/MainLayout/selectors';
import React, { FC, lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { compose, Dispatch } from 'redux';

import useProductDetailContent from 'components/Image/useProductDetailContent';
import LoadingIndicator from 'components/LoadingIndicator';
import { push } from 'connected-react-router';
import { setFavorite, setProductNote } from 'containers/MainLayout/actions';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import METADATA from 'containers/MarketPage/metadata';
import { appLocales } from 'i18n';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import translations from 'translations';
import { ProductNoteInput, Product as ProductType, Store } from 'types/schema';
import generateProductSlug from 'utils/generateProductSlug';
import { getLocalOriginName } from 'utils/getLocalOriginName';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import renderWithWebDomain from 'utils/renderWithWebDomain';
import formatMoney from '../../utils/formatMoney';
import { getUomNameLocal } from '../../utils/getUomNameLocal';
import { CUSTOM_DESCRIPTION, DESCRIPTION_BY_LANGUAGE } from './constants';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import selectProductDetailsPage from './selectors';
import { ContainerState, RootState } from './types';
import calculateVAT from 'utils/calculateVAT';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import useScrollTimingTracking from 'hooks/useScrollTimingTracking';

export interface IAddedProduct {
  [productId: string]: number;
}
export interface IAddingProduct {
  [productId: string]: boolean;
}

interface StateProps {
  productDetailsPage: ContainerState;
  store: Store;
  addedProducts: IAddedProduct;
  addingProducts: IAddingProduct;
  permissions: string[];
  breadCrumbDataMarkup: string;
}

interface DispatchProps {
  onToggleFavorite: (data: { product: ProductType }) => void;
  setProductNote: (data: ProductNoteInput) => void;
  gotoSignIn: () => void;
}

type Props = StateProps & DispatchProps;

const ProductDetailsContainer = styled.div`
  padding: 32px 0px;
  @media (max-width: 767px) {
    padding: 8px 16px 16px;
  }
  background: #ffffff;
`;

export const SkeletonWrapper = styled.div`
  min-height: 380px;
  width: 100%;
`;

const Product = lazy(
  () =>
    import(
      /* webpackPreload: true */
      'components/Product/Product'
    ),
);

const ModalNote = lazy(() => import('components/Checkout/ModalNote'));

const ProductDetailsPage: FC<Props> = (props) => {
  const {
    permissions,
    addingProducts,
    addedProducts,
    productDetailsPage: {
      product: productDetails,
      relateProducts,
      noteItemLoading,
      productsInSameProductGroups,
      productsInSameProductFamily,
      vouchersForProduct,
      productReviews,
    },
    onToggleFavorite,
    setProductNote,
    gotoSignIn,
    breadCrumbDataMarkup,
  } = props;

  const {
    id,
    name,
    nameLocal,
    description,
    imageUrl,
    category,
    supplierInternalCode,
    vat,
    uomLocal,
    inStock,
    preservation,
    supplier,
    origin,
    details,
    validProductUrl,
    alternateProductUrl,
    localLang,
    alternateLang,
  } = productDetails;

  const loggedIn = !!localStorage.getItem('token');
  const [previewProduct, setPreviewProduct] = useState<ProductType>();
  const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
  const productDetailRef = useRef<HTMLElement>(null);
  const onClickOpenNoteModal = useCallback<(product: ProductType) => void>(
    (product) => {
      if (loggedIn) {
        setPreviewProduct(product);
        setIsOpenNoteModal(true);
      } else {
        gotoSignIn();
      }
    },
    [setIsOpenNoteModal, setPreviewProduct],
  );

  const onClickDeleteNote = useCallback(
    (product) => {
      setProductNote({ productId: product.id, note: '' });
    },
    [setProductNote],
  );

  const onClickCloseNoteModal = useCallback(() => {
    setPreviewProduct(undefined);
    setIsOpenNoteModal(false);
  }, [setPreviewProduct, setIsOpenNoteModal]);

  const onSubmitAddingNote = useCallback(
    (note) => {
      if (previewProduct?.id) {
        setProductNote({ productId: previewProduct.id, note: note });
      }
      onClickCloseNoteModal();
    },
    [previewProduct, setProductNote, onClickCloseNoteModal],
  );

  const customDescriptionValue = useMemo(() => {
    if (appLocales.includes(localLang)) {
      const customDescription = {
        [DESCRIPTION_BY_LANGUAGE[localLang].supplied]: supplier?.name,
        [DESCRIPTION_BY_LANGUAGE[localLang].origin]:
          origin === 'Other'
            ? CUSTOM_DESCRIPTION.origin[localLang][0]
            : `${CUSTOM_DESCRIPTION.origin[localLang][1]} ${getLocalOriginName(origin || '')}`,
        [DESCRIPTION_BY_LANGUAGE[localLang].price]: `${formatMoney(
          Number(
            calculateVAT(
              productDetails?.priceV2?.price || 0,
              productDetails?.priceV2?.vat || 0,
              !!productDetails?.priceV2?.vatIncluded,
            ).beforeVat || 0,
          ),
        )}/${getUomNameLocal(uomLocal || '')?.toLowerCase()}`,
        VAT: `${vat}%`,
        [DESCRIPTION_BY_LANGUAGE[localLang].preservation]:
          preservation === 'Frozen'
            ? CUSTOM_DESCRIPTION.preservation[localLang][0]
            : preservation === 'Refrigerated'
              ? CUSTOM_DESCRIPTION.preservation[localLang][1]
              : CUSTOM_DESCRIPTION.preservation[localLang][2],
        [DESCRIPTION_BY_LANGUAGE[localLang].status]: inStock
          ? CUSTOM_DESCRIPTION.status[localLang][0]
          : CUSTOM_DESCRIPTION.status[localLang][1],
        [DESCRIPTION_BY_LANGUAGE[localLang].unit]: `(${uomLocal?.toLowerCase()})`,
        [DESCRIPTION_BY_LANGUAGE[localLang].category]: String(category?.nameLocal)
          .replace('&', `${localLang === 'vi' ? 'và' : 'and'}`)
          .toLowerCase(),
      };
      return `${name} ${Object.keys(customDescription).reduce((accumulate, item, index) => {
        return index === Object.keys(customDescription).length - 1
          ? `${accumulate}${item} ${customDescription[item]}`
          : `${accumulate}${item} ${customDescription[item]}, `;
      }, '')}. ${METADATA[`${category?.parent?.name}/${category?.name}`]?.[localLang]?.description}`;
    }
    return '';
  }, [productDetails, localLang]);

  const productMarkup = useMemo(() => {
    let result;
    if (category) {
      result = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${name ?? ''} | KAMEREO`,
        image: [imageUrl ?? 'https://kamereo.vn/static/images/kamereo_1.jpg'],
        /* ideal length would be 70-155 characters for description */
        description: description && description.length > 70 ? description : customDescriptionValue,
        sku: supplierInternalCode,
        brand: {
          '@type': 'Brand',
          name: 'Kamereo',
        },
        offers: {
          '@type': 'Offer',
          url: renderWithWebDomain(process.env.APP_ENV, `/products/${validProductUrl}`),
          price:
            Number(
              calculateVAT(
                productDetails?.priceV2?.price || 0,
                productDetails?.priceV2?.vat || 0,
                !!productDetails?.priceV2?.vatIncluded,
              ).beforeVat || 0,
            ).toFixed(2) ?? 0.0,
          priceCurrency: 'VND',
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'Kamereo',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: 0.0,
              currency: 'VND',
            },
            shippingDestination: [
              {
                '@type': 'DefinedRegion',
                addressCountry: 'VN',
                addressRegion: ['Ho Chi Minh City'],
              },
            ],
          },
        },
        // review: {
        //   '@type': 'Review',
        //   reviewRating: {
        //     '@type': 'Rating',
        //     ratingValue: randomRating,
        //     bestRating: 5,
        //   },
        // },
        // aggregateRating: {
        //   '@type': 'AggregateRating',
        //   ratingValue: ((randomRating + 5) / 2).toFixed(1),
        //   reviewCount: getRandomNumber(70, 130),
        // },
      };
    }
    return JSON.stringify(result);
  }, [productDetails, category]);

  const productDetailData = useProductDetailContent(details || '', name || '');

  const handleScrollEvent = (percent: number, duration: number) => {
    sendGA4Event(EventName.PDP_SCROLL_DEPTH, {
      product_id: id,
      percent_scrolled: percent,
      duration_ms: duration,
    });
  };

  useScrollTimingTracking(handleScrollEvent);

  return (
    <ProductDetailsContainer>
      <Helmet>
        <title>{`${name} | KAMEREO`}</title>
        <meta property="og:url" content={renderWithWebDomain(process.env.APP_ENV, `/products/${validProductUrl}`)} />
        <link
          rel="canonical"
          href={renderWithWebDomain(
            process.env.APP_ENV,
            `/products/${localLang === 'vi' ? validProductUrl : alternateProductUrl}`,
          )}
        />
        <link
          rel="alternate"
          hrefLang={localLang}
          href={renderWithWebDomain(process.env.APP_ENV, `/products/${validProductUrl}`)}
        />
        <link
          rel="alternate"
          hrefLang={alternateLang}
          href={renderWithWebDomain(process.env.APP_ENV, `/products/${alternateProductUrl}`)}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={renderWithWebDomain(
            process.env.APP_ENV,
            `/products/${generateProductSlug(nameLocal?.['vi'], supplierInternalCode)}`,
          )}
        />
        <meta
          name="description"
          content={`${description && description.length > 70 ? description : customDescriptionValue}`}
        />
        <meta
          name="og:description"
          content={`${description && description.length > 70 ? description : customDescriptionValue}`}
        />
        <meta property="og:title" content={`${name || translations(messages.header)} | KAMEREO`} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="Kamereo" />
        <meta property="og:image:url" content={imageUrl || getImageByCategory(category)} />
        <meta property="og:image:secure_url" content={imageUrl || getImageByCategory(category)} />
        <meta property="og:image:alt" content={name || translations(messages.header)} />
        <meta
          property="og:price:amount"
          content={String(
            calculateVAT(
              productDetails?.priceV2?.price || 0,
              productDetails?.priceV2?.vat || 0,
              !!productDetails?.priceV2?.vatIncluded,
            ).beforeVat || 0,
          )}
        />
        <meta property="og:price:currency" content={String('VND')} />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:title" content={name || translations(messages.header)} />
        <meta
          property="twitter:description"
          content={description && description.length > 70 ? description : customDescriptionValue}
        />
        <meta property="twitter:image" content={imageUrl || getImageByCategory(category)} />
        <meta property="twitter:image:alt" content={name || translations(messages.header)} />
        <meta name="robots" content={process.env.APP_ENV === 'production' ? 'index, follow' : 'noindex, nofollow'} />
        <script type="application/ld+json">{breadCrumbDataMarkup}</script>
        <script type="application/ld+json">{productMarkup}</script>
      </Helmet>
      <Product
        ref={productDetailRef}
        added={addedProducts[id]}
        adding={addingProducts[id]}
        addedRelatedProducts={addedProducts}
        addingRelatedProducts={addingProducts}
        product={productDetails}
        permissions={permissions}
        gotoSignIn={gotoSignIn}
        onToggleFavorite={onToggleFavorite}
        onOpenNote={onClickOpenNoteModal}
        onDeleteNote={onClickDeleteNote}
        kamereoProducts={productsInSameProductGroups}
        productsInSameProductFamily={productsInSameProductFamily}
        relatedProducts={relateProducts}
        isHaveProductDetailsDescription={!!productDetailData?.description}
        vouchersForProduct={vouchersForProduct}
        productReviews={productReviews}
      />
      {isOpenNoteModal && (
        <Suspense fallback={<LoadingIndicator />}>
          <ModalNote
            headerLabel={previewProduct?.name || ''}
            isOpen={isOpenNoteModal}
            confirmLoading={noteItemLoading}
            onSubmit={onSubmitAddingNote}
            onClose={onClickCloseNoteModal}
            note={previewProduct?.note || ''}
          />
        </Suspense>
      )}
    </ProductDetailsContainer>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  productDetailsPage: selectProductDetailsPage(),
  store: selectStore(),
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
  breadCrumbDataMarkup: selectBreadCrumbDataMarkup(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onToggleFavorite: (data) => dispatch(setFavorite(data)),
    setProductNote: (data) => dispatch(setProductNote(data)),
    gotoSignIn: () => dispatch(push('/signin')),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer<Props>({
  key: 'productDetailsPage',
  reducer: reducer,
});

const withSaga = injectSaga<Props>({
  key: 'productDetailsPage',
  saga: saga,
});

export default compose(withReducer, withSaga, withConnect)(ProductDetailsPage);
